<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'CreateLotteryConfig' }" class="btn btn-primary">
                <i class="ri-add-line"></i>
                新增抽獎工具
              </router-link>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋抽獎名稱..."
          @input="handleSearch"
        />
      </div>
      <div class="filter-group">
        <select v-model="filterActive" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="true">啟用</option>
          <option value="false">停用</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filterToolType" class="form-control" @change="handleFilterChange">
          <option value="">全部工具類型</option>
          <option value="wheel">轉盤</option>
          <option value="scratch" disabled>刮刮樂</option>
          <option value="mario" disabled>小瑪利</option>
          <option value="draw" disabled>抽籤</option>
          <option value="scratch_card" disabled>搓搓樂</option>
          <option value="other" disabled>其他</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchConfigs">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>抽獎名稱</th>
            <th>工具類型</th>
            <th>狀態</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="configs.length === 0">
            <td colspan="5" class="empty-state">
              沒有找到抽獎配置
            </td>
          </tr>
          <tr v-for="config in configs" :key="config.id">
            <td>{{ config.configName }}</td>
            <td>
              <span class="badge badge-info">{{ getToolTypeText(config.toolType) }}</span>
            </td>
            <td>
              <span :class="config.isActive ? 'badge badge-success' : 'badge badge-secondary'">
                {{ config.isActive ? '啟用' : '停用' }}
              </span>
            </td>
            <td>{{ formatDate(config.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <router-link
                  :to="{ name: 'EditLotteryConfig', params: { id: config.id } }"
                  class="btn btn-sm btn-primary"
                >
                  <i class="ri-edit-line"></i>
                  編輯
                </router-link>
                <button 
                  v-if="config.isActive"
                  class="btn btn-sm btn-warning" 
                  @click="handleDisable(config.id)"
                >
                  <i class="ri-pause-circle-line"></i>
                  停用
                </button>
                <button 
                  v-else
                  class="btn btn-sm btn-danger" 
                  @click="handleDelete(config.id)"
                >
                  <i class="ri-delete-bin-line"></i>
                  刪除
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- 分頁 -->
      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="currentPage"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="goToPage"
      />
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { lotteryService } from '@/api/modules/lottery.js';
import { showError, showDeleteConfirm, showSuccess } from '@/utils/swal.js';

export default {
  name: 'LotteryConfigList',
  setup() {
    const { pageTitle } = usePageTitle('抽獎工具管理');
    const configs = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterActive = ref('');
    const filterToolType = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const pagination = reactive({
      totalItems: 0,
      totalPages: 1
    });


    const fetchConfigs = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }

        if (filterActive.value !== '') {
          params.isActive = filterActive.value === 'true';
        }

        if (filterToolType.value) {
          params.toolType = filterToolType.value;
        }

        const response = await lotteryService.getConfigs(params);

        if (response && response.success && response.data) {
          configs.value = response.data.items || [];
          pagination.totalItems = response.data.pagination?.totalItems || 0;
          pagination.totalPages = response.data.pagination?.totalPages || 1;
        } else {
          error.value = response?.message || '獲取抽獎配置列表失敗';
        }
      } catch (err) {
        console.error('獲取抽獎配置列表失敗:', err);
        error.value = err.message || '獲取抽獎配置列表失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      fetchConfigs();
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchConfigs();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= pagination.totalPages) {
        currentPage.value = page;
        fetchConfigs();
      }
    };


    const handleDisable = async (configId) => {
      const config = configs.value.find(c => c.id === configId);
      const configName = config ? config.configName : '此配置';

      try {
        const response = await lotteryService.updateConfig(configId, {
          isActive: false
        });

        if (response && response.success) {
          await showSuccess('停用成功', '抽獎配置已停用');
          await fetchConfigs();
        } else {
          await showError('停用失敗', response?.message || '停用抽獎配置失敗');
        }
      } catch (err) {
        console.error('停用抽獎配置失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '停用抽獎配置失敗';
        await showError('停用失敗', errorMessage);
      }
    };

    const handleDelete = async (configId) => {
      const config = configs.value.find(c => c.id === configId);
      const configName = config ? config.configName : '此配置';

      try {
        const confirmed = await showDeleteConfirm(configName, [
          '抽獎紀錄',
          '獎品設定',
          '每日統計資料'
        ]);
        if (!confirmed) return;

        const response = await lotteryService.deleteConfig(configId);

        if (response && response.success) {
          await showSuccess('刪除成功', '抽獎配置已刪除');
          await fetchConfigs();
        } else {
          await showError('刪除失敗', response?.message || '刪除抽獎配置失敗');
        }
      } catch (err) {
        console.error('刪除抽獎配置失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '刪除抽獎配置失敗';
        await showError('刪除失敗', errorMessage);
      }
    };

    const getToolTypeText = (toolType) => {
      const typeMap = {
        wheel: '轉盤',
        scratch: '刮刮樂',
        mario: '小瑪利',
        draw: '抽籤',
        scratch_card: '搓搓樂',
        other: '其他'
      };
      return typeMap[toolType] || toolType;
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW');
    };

    onMounted(() => {
      fetchConfigs();
    });

    return {
      pageTitle,
      configs,
      loading,
      error,
      searchQuery,
      filterActive,
      filterToolType,
      currentPage,
      pagination,
      fetchConfigs,
      handleSearch,
      handleFilterChange,
      goToPage,
      handleDisable,
      handleDelete,
      getToolTypeText,
      formatDate
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper {
  background: white;
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: visible;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  -webkit-overflow-scrolling: touch; /* iOS 平滑滾動 */
}

table {
  width: 100%;
  min-width: 600px; /* 確保表格最小寬度，手機版可橫向滾動 */
  border-collapse: collapse;
}

thead {
  background-color: #f5f5f5;
}

th,
td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

th {
  font-weight: 600;
  color: #333;
}

tbody tr:hover {
  background-color: #f9f9f9;
}

.empty-state {
  text-align: center;
  color: #999;
  padding: 40px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 14px;
}

.btn-warning {
  background: #ffc107;
  color: #333;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-warning:hover:not(:disabled) {
  background: #ffb300;
}

.btn-warning:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 20px;
}

/* 彈窗樣式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-dialog {
  background: white;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content {
  padding: 0;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

</style>
